import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { clearStateSnapshot } from '@/app/store/stateSnapshot/stateSnapshotPersistence';
import { SESSION_BROADCAST_CHANNEL, type SessionBroadcastMessage } from '@/config/sessionConfig';
import { logout } from '@/features/auth/authSlice';

import { useIdleTimer } from './useIdleTimer';
import { useSessionConfig } from './useSessionConfig';

import type { RootState } from '@/app/store/store';

/**
 * useSessionManager
 *
 * Orchestrates application-wide session lifecycle:
 *
 *  - Idle detection via useIdleTimer (only when authenticated)
 *  - Warning modal state + countdown when idle threshold is crossed
 *  - "Continue" → token refresh + timer reset + broadcast to other tabs
 *  - "Logout" / countdown expiry → Redux logout + navigate to /login + broadcast
 *  - Cross-tab synchronisation via BroadcastChannel
 *
 * Mount once at app-shell level via SessionManagerBootstrap.
 */
export function useSessionManager() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticated = useSelector((state: RootState) => state.authStore.isAuthenticated);

    // ── Session timeouts from backend cache ────────────────────────────────────
    const { timeoutMs, warningMs } = useSessionConfig();

    // ── Warning modal state ────────────────────────────────────────────────────
    const [showWarning, setShowWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.floor(warningMs / 1000));

    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isHandlingLogoutRef = useRef(false);
    const wasAuthenticatedRef = useRef(isAuthenticated);

    // ── BroadcastChannel ───────────────────────────────────────────────────────
    const channelRef = useRef<BroadcastChannel | null>(null);

    // A single ref that always points to the latest "handle remote refresh" logic.
    // The BroadcastChannel onmessage handler is registered once and must not close
    // over stale state — reading through this ref ensures it always executes the
    // current version of resetTimer / stopCountdown without re-registering the channel.
    const handleRemoteRefreshRef = useRef<() => void>(() => {});

    // ── Helpers ────────────────────────────────────────────────────────────────

    const stopCountdown = useCallback(() => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    }, []);

    const broadcast = useCallback((message: SessionBroadcastMessage) => {
        channelRef.current?.postMessage(message);
    }, []);

    const performLogout = useCallback(
        (source: 'idle' | 'user' | 'remote') => {
            if (isHandlingLogoutRef.current) return;
            isHandlingLogoutRef.current = true;

            stopCountdown();
            setShowWarning(false);

            if (source !== 'remote') {
                broadcast({ type: 'logout' });
            }

            // Clear the localStorage snapshot immediately so that a freshly opened
            // tab after logout cannot hydrate a stale authenticated state.
            clearStateSnapshot();
            dispatch(logout());
        },
        [dispatch, stopCountdown, broadcast],
    );

    // ── Idle timer callbacks ───────────────────────────────────────────────────

    const handleIdleWarning = useCallback(() => {
        setSecondsLeft(Math.floor(warningMs / 1000));
        setShowWarning(true);

        stopCountdown();
        countdownRef.current = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    stopCountdown();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [stopCountdown, warningMs]);

    const handleIdleExpire = useCallback(() => {
        performLogout('idle');
    }, [performLogout]);

    const { resetTimer } = useIdleTimer({
        onWarning: handleIdleWarning,
        onExpire: handleIdleExpire,
        enabled: isAuthenticated,
        timeoutMs,
        warningMs,
    });

    // Keep the remote-refresh ref in sync every render so the BroadcastChannel
    // handler always calls the up-to-date stopCountdown / resetTimer.
    useEffect(() => {
        handleRemoteRefreshRef.current = () => {
            stopCountdown();
            resetTimer();
            setShowWarning(false);
        };
    });

    // Auto-logout when countdown reaches zero (user ignored the warning).
    useEffect(() => {
        if (secondsLeft === 0 && showWarning) {
            performLogout('idle');
        }
    }, [secondsLeft, showWarning, performLogout]);

    // ── User actions ───────────────────────────────────────────────────────────

    const handleContinue = useCallback(() => {
        // Dismiss modal, reset idle clock, tell other tabs to do the same.
        // Token validity is managed solely by TokenRefreshBootstrap — calling
        // a second useTokenRefresh instance here would create a duplicate hook
        // with a stale token closure that dispatches logout() unpredictably.
        stopCountdown();
        setShowWarning(false);
        resetTimer();
        broadcast({ type: 'refresh' });
    }, [stopCountdown, resetTimer, broadcast]);

    const handleLogout = useCallback(() => {
        performLogout('user');
    }, [performLogout]);

    // ── Cross-tab synchronisation ──────────────────────────────────────────────

    useEffect(() => {
        if (!isAuthenticated) return;

        // Guard: BroadcastChannel may not be available in all environments.
        if (typeof BroadcastChannel === 'undefined') return;

        const channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
        channelRef.current = channel;

        channel.onmessage = (event: MessageEvent<SessionBroadcastMessage>) => {
            const message = event.data;
            if (message.type === 'logout') {
                performLogout('remote');
            } else if (message.type === 'refresh') {
                // Another tab extended the session — call through the ref so we
                // always hit the current stopCountdown/resetTimer, not a stale closure.
                handleRemoteRefreshRef.current();
            }
        };

        return () => {
            channel.close();
            channelRef.current = null;
        };
    }, [isAuthenticated, performLogout]);

    // Reset the logout guard whenever authenticated state changes.
    useEffect(() => {
        if (isAuthenticated) {
            isHandlingLogoutRef.current = false;
        }
    }, [isAuthenticated]);

    // Cover logout actions from every source, including API/token expiry and
    // Redux logout messages received from another tab.
    useEffect(() => {
        if (wasAuthenticatedRef.current && !isAuthenticated) {
            stopCountdown();
            setShowWarning(false);
            if (location.pathname !== '/login') {
                navigate('/login', { replace: true });
            }
        }

        wasAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated, location.pathname, navigate, stopCountdown]);

    // Tear down the countdown when the component unmounts.
    useEffect(() => () => stopCountdown(), [stopCountdown]);

    return {
        showWarning,
        secondsLeft,
        handleContinue,
        handleLogout,
    };
}
